/**
 * Cornerstone v2 preview screens (Phase 2 batch):
 *   • ScheduleV2   — /preview/parent/schedule
 *   • InvoicesV2   — /preview/parent/invoices
 *   • MessagesV2   — /preview/parent/messages
 *   • MoreV2       — /preview/parent/more
 *
 * All reuse the shared PreviewShell + BottomNav + StatusPill + Card.
 * All fetch from EXISTING backend endpoints (no new APIs, no business
 * logic changes). If the user taps a live action (Pay, mark read, etc.)
 * they land on the current v1 page — a soft transition until Phase 3.
 */
import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  CalendarDays, Clock, User as UserIcon, MapPin, FileText, ChevronRight,
  Search, Megaphone, Bell, HelpCircle, Info, Phone, LogOut, Baby,
  Settings, Wallet as WalletIcon, MessageSquare,
} from "lucide-react";
import { useList, fmtRM, dayName } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Spinner } from "@/components/common";
import { PreviewShell, Card, StatusPill } from "./_shell.jsx";

/* ─── SCHEDULE ─────────────────────────────────────────────────── */
export function ScheduleV2Preview() {
  const { data: schedules, loading } = useList("/schedules");
  const [tab, setTab] = useState("upcoming");
  if (loading) return <PreviewShell title="Schedule"><Loading /></PreviewShell>;

  const list = schedules || [];
  return (
    <PreviewShell title="Schedule" subtitle="This week">
      <div className="grid grid-cols-2 rounded-full bg-[color:var(--v2-soft-grey)] p-1 mb-4" data-testid="v2-schedule-tabs">
        {["upcoming", "past"].map((t) => (
          <button key={t}
                  onClick={() => setTab(t)}
                  className={"rounded-full py-2 text-sm font-medium capitalize " + (tab === t ? "bg-white text-[color:var(--v2-primary)] shadow-sm" : "text-[color:var(--v2-text-muted)]")}
                  data-testid={`v2-tab-${t}`}>
            {t}
          </button>
        ))}
      </div>
      {list.length === 0 ? (
        <EmptyState icon={CalendarDays} title="No classes scheduled" hint="Your weekly lessons will show up here." />
      ) : (
        <ul className="space-y-3" data-testid="v2-schedule-list">
          {list.map((s) => (
            <li key={s.id} data-testid={`v2-schedule-${s.id}`}>
              <Card className="p-4 flex gap-3 items-center">
                <div className="w-16 shrink-0 text-center">
                  <p className="text-xs text-[color:var(--v2-text-muted)] uppercase tracking-wide">{dayName(s.day_of_week).slice(0,3)}</p>
                  <p className="font-display text-xl font-semibold leading-none mt-0.5">{s.start_time?.slice(0,5) || "—"}</p>
                </div>
                <div className="flex-1 min-w-0 border-l border-[color:var(--v2-border)] pl-3">
                  <p className="font-semibold text-sm truncate">{s.course_name || s.student_name}</p>
                  <p className="text-xs text-[color:var(--v2-text-muted)] mt-0.5 truncate">
                    <UserIcon className="h-3 w-3 inline mr-0.5 -mt-0.5" />{s.teacher_name}
                  </p>
                  <p className="text-xs text-[color:var(--v2-text-muted)] truncate">
                    <MapPin className="h-3 w-3 inline mr-0.5 -mt-0.5" />{s.studio_name}
                  </p>
                </div>
                <StatusPill status="Confirmed" />
              </Card>
            </li>
          ))}
        </ul>
      )}
      <Link to="/parent/leave"
            className="mt-5 block text-center rounded-full border border-[color:var(--v2-primary)] text-[color:var(--v2-primary)] py-2.5 text-sm font-semibold hover:bg-[color:var(--v2-light-blue)] transition-colors"
            data-testid="v2-request-leave">
        Request Leave
      </Link>
    </PreviewShell>
  );
}

/* ─── INVOICES ─────────────────────────────────────────────────── */
export function InvoicesV2Preview() {
  const { data: invoices, loading } = useList("/invoices");
  if (loading) return <PreviewShell title="Invoices"><Loading /></PreviewShell>;
  const list = invoices || [];
  const unpaid = list.filter((i) => i.status !== "paid");
  const total = unpaid.reduce((s, i) => s + (i.outstanding_amount || 0), 0);

  return (
    <PreviewShell title="Invoices & Payments" subtitle={`${unpaid.length} outstanding`}>
      {/* summary hero */}
      <section className="rounded-2xl p-5 mb-5 text-white"
        style={{ background: "linear-gradient(135deg, #3E1C5E 0%, #5B2A86 100%)" }}
        data-testid="v2-invoice-summary">
        <p className="text-sm text-white/80 mb-1">Total outstanding</p>
        <p className="font-display text-3xl font-bold">{fmtRM(total)}</p>
        <p className="text-xs text-white/70 mt-1">Across {unpaid.length} invoice(s)</p>
        {unpaid.length > 0 && (
          <Link to="/parent/invoices"
                className="mt-4 inline-flex items-center justify-center rounded-full bg-white text-[color:var(--v2-primary)] px-5 py-2 text-sm font-semibold hover:-translate-y-0.5 transition-transform"
                data-testid="v2-pay-all">
            Pay all now
          </Link>
        )}
      </section>

      {list.length === 0 ? (
        <EmptyState icon={FileText} title="No invoices" hint="Your invoices will appear here." />
      ) : (
        <ul className="space-y-3" data-testid="v2-invoice-list">
          {list.slice(0, 20).map((i) => (
            <li key={i.id} data-testid={`v2-invoice-${i.id}`}>
              <Card className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-mono text-xs text-[color:var(--v2-text-muted)]">{i.invoice_number}</p>
                    <p className="font-semibold text-sm truncate">{i.student_name}</p>
                    <p className="text-[11px] text-[color:var(--v2-text-muted)] mt-0.5">Due {i.due_date}</p>
                  </div>
                  <div className="text-right">
                    <p className={"font-bold text-base " + (i.status === "paid" ? "text-emerald-600" : "text-[color:var(--v2-danger)]")}>
                      {fmtRM(i.outstanding_amount || i.total)}
                    </p>
                    <div className="mt-1"><StatusPill status={i.status} /></div>
                  </div>
                </div>
                {i.status !== "paid" && (
                  <Link to="/parent/invoices"
                        className="mt-3 flex items-center justify-center rounded-full bg-[color:var(--v2-primary)] hover:bg-[color:var(--v2-primary-dark)] transition-colors text-white text-xs font-semibold py-2"
                        data-testid={`v2-pay-${i.id}`}>
                    Pay Now
                  </Link>
                )}
              </Card>
            </li>
          ))}
        </ul>
      )}
    </PreviewShell>
  );
}

/* ─── MESSAGES ─────────────────────────────────────────────────── */
export function MessagesV2Preview() {
  const { data: notifications, loading } = useList("/notifications");
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const list = notifications || [];
    if (!q.trim()) return list;
    const needle = q.toLowerCase();
    return list.filter((n) => (n.title || "").toLowerCase().includes(needle)
                           || (n.body || "").toLowerCase().includes(needle));
  }, [notifications, q]);
  if (loading) return <PreviewShell title="Messages"><Loading /></PreviewShell>;

  return (
    <PreviewShell title="Messages" subtitle={`${(notifications || []).filter(n => !n.is_read).length} unread`}>
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[color:var(--v2-text-muted)]" />
        <input value={q}
               onChange={(e) => setQ(e.target.value)}
               placeholder="Search messages"
               className="w-full rounded-full border border-[color:var(--v2-border)] bg-[color:var(--v2-soft-grey)] pl-9 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--v2-accent-blue)]"
               data-testid="v2-msg-search" />
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={MessageSquare} title="No messages" hint={q ? "No results for your search." : "You’re all caught up."} />
      ) : (
        <ul className="space-y-2" data-testid="v2-msg-list">
          {filtered.slice(0, 50).map((n) => (
            <li key={n.id} data-testid={`v2-msg-${n.id}`}>
              <Card className={"p-3 flex gap-3 items-start " + (!n.is_read ? "bg-[color:var(--v2-light-blue)]/40 border-[color:var(--v2-accent-blue)]/40" : "")}>
                <div className="h-10 w-10 rounded-full bg-[color:var(--v2-light-blue)] flex items-center justify-center shrink-0">
                  <Megaphone className="h-5 w-5 text-[color:var(--v2-primary)]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-semibold text-sm truncate">{n.title || "Notification"}</p>
                    <p className="text-[10px] text-[color:var(--v2-text-muted)] shrink-0">
                      {(n.created_at || "").slice(5, 10)}
                    </p>
                  </div>
                  <p className="text-xs text-[color:var(--v2-text-muted)] mt-0.5 line-clamp-2">{n.body}</p>
                </div>
                {!n.is_read && <div className="h-2 w-2 rounded-full bg-[color:var(--v2-primary)] mt-2" />}
              </Card>
            </li>
          ))}
        </ul>
      )}
    </PreviewShell>
  );
}

/* ─── MORE / PROFILE ───────────────────────────────────────────── */
export function MoreV2Preview() {
  const { user } = useAuth() || {};
  const { data: overview } = useList("/parent/overview");
  const initials = ((user?.full_name || overview?.parent?.full_name || "You")
    .split(" ").map(p => p[0]).slice(0,2).join("")).toUpperCase();
  const rows = [
    { icon: UserIcon, label: "My Profile", to: "/parent" },
    { icon: Baby, label: "My Children", to: "/parent/children" },
    { icon: CalendarDays, label: "Schedule", to: "/preview/parent/schedule" },
    { icon: WalletIcon, label: "Payments", to: "/preview/parent/invoices" },
    { icon: Bell, label: "Notification Settings", to: "/parent/notifications" },
    { icon: Settings, label: "Preferences", to: "/parent" },
    { icon: HelpCircle, label: "Help & FAQ", to: "/enquiry" },
    { icon: Info, label: "About Cornerstone", to: "/" },
    { icon: Phone, label: "Contact Us", to: "/enquiry" },
  ];
  return (
    <PreviewShell title="More">
      <Card className="p-4 flex items-center gap-3 mb-4" data-testid="v2-profile-card">
        <div className="h-14 w-14 rounded-full bg-[color:var(--v2-light-blue)] flex items-center justify-center border border-[color:var(--v2-border)]">
          <span className="font-display font-semibold text-lg text-[color:var(--v2-primary)]">{initials}</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold truncate">{user?.full_name || overview?.parent?.full_name || "Parent"}</p>
          <p className="text-xs text-[color:var(--v2-text-muted)] truncate">{user?.email || overview?.parent?.email || ""}</p>
        </div>
        <ChevronRight className="h-4 w-4 text-[color:var(--v2-text-muted)]" />
      </Card>

      <Card className="divide-y divide-[color:var(--v2-border)]">
        {rows.map((r) => (
          <Link key={r.label} to={r.to}
                className="flex items-center gap-3 px-4 py-3.5 hover:bg-[color:var(--v2-soft-grey)] transition-colors"
                data-testid={`v2-more-${r.label.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}>
            <div className="h-9 w-9 rounded-full bg-[color:var(--v2-light-blue)] flex items-center justify-center">
              <r.icon className="h-4 w-4 text-[color:var(--v2-primary)]" />
            </div>
            <span className="flex-1 text-sm font-medium">{r.label}</span>
            <ChevronRight className="h-4 w-4 text-[color:var(--v2-text-muted)]" />
          </Link>
        ))}
      </Card>

      <button onClick={() => { window.location.href = "/logout"; }}
              className="mt-4 w-full flex items-center justify-center gap-2 rounded-full border border-[color:var(--v2-danger)]/30 text-[color:var(--v2-danger)] py-2.5 text-sm font-semibold hover:bg-[color:var(--v2-danger-bg)] transition-colors"
              data-testid="v2-more-logout">
        <LogOut className="h-4 w-4" /> Log out
      </button>

      <p className="text-center text-xs text-[color:var(--v2-text-muted)] mt-4">Cornerstone Music · v2 preview</p>
    </PreviewShell>
  );
}

/* ─── shared helpers ───────────────────────────────────────────── */
function Loading() {
  return <div className="min-h-[60vh] flex items-center justify-center"><Spinner /></div>;
}
function EmptyState({ icon: Icon, title, hint }) {
  return (
    <div className="text-center py-16">
      <div className="h-14 w-14 rounded-full bg-[color:var(--v2-light-blue)] flex items-center justify-center mx-auto mb-3">
        <Icon className="h-6 w-6 text-[color:var(--v2-primary)]" />
      </div>
      <p className="font-semibold">{title}</p>
      {hint && <p className="text-xs text-[color:var(--v2-text-muted)] mt-1">{hint}</p>}
    </div>
  );
}
